import React, { useRef } from "react";

import { FeaturesBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { FeatureValueProp } from "./FeatureValueProp";
import {
    HotspotWithAlignment,
    useImageBounds,
    useValuePropAdjustments,
} from "./hooks";
import { baseAdjustment } from "./utils";

import styles from "./index.module.scss";

interface Props {
    value: Value;
}

interface CustomCss extends React.CSSProperties {
    "--top"?: string;
    "--left"?: string;
    "--width"?: string;
}

/**
 * There is a mountain of complexity in here because the design wants straight
 * lines from value props to hotspots when possible
 */
export const FeaturesBlock = ({
    value: { hotspot_image, value_props },
}: Props) => {
    const imageWrapperRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const valuePropRefs = useRef<(HTMLDivElement | null)[]>([]);

    const imageBounds = useImageBounds(
        imageWrapperRef,
        innerRef,
        hotspot_image?.image ?? null,
    );

    /**
     * If you use just this then there are problems if there are more hotspots
     * than value props. So we set a little sanity check in the frontend here.
     */
    const _hotspots = hotspot_image?.hotspots || null;
    const hotspots = _hotspots?.slice(0, value_props?.length || 0) || null;

    const hotspotWithAlignment: HotspotWithAlignment[] | null =
        hotspots?.map((hotspot, index) => ({
            ...hotspot,
            alignment: value_props?.[index].alignment || null,
        })) || null;

    const adjustments = useValuePropAdjustments(
        valuePropRefs,
        innerRef,
        imageBounds,
        hotspotWithAlignment,
    );

    if (!hotspot_image?.image || !hotspots?.length) return null;

    return (
        <section className={styles.root}>
            <div className={styles.inner} ref={innerRef}>
                <div className={styles.hotspotImage} ref={imageWrapperRef}>
                    <ImageChooserBlock value={hotspot_image.image} />
                </div>
                <div className={styles.hotspotMap}>
                    {hotspots.map(({ x, y, id }) => {
                        if (!imageBounds) return;
                        const top = `${imageBounds.top + y * imageBounds.height}px`;
                        const left = `${imageBounds.left + x * imageBounds.width}px`;
                        const style: CustomCss = {
                            "--top": top,
                            "--left": left,
                        };

                        return (
                            <div
                                key={`hotspot-${id}`}
                                className={styles.hotspot}
                                style={style}
                            >
                                {id}
                            </div>
                        );
                    })}
                </div>
                <div>
                    {value_props?.map((prop, index) => {
                        const adjustment = adjustments[index] || baseAdjustment;

                        return (
                            <FeatureValueProp
                                hotspot={hotspots[index]}
                                imageBounds={imageBounds}
                                index={index}
                                key={`value-prop-${index}`}
                                value={prop}
                                containerRect={
                                    innerRef.current?.getBoundingClientRect() ||
                                    null
                                }
                                ref={(element) => {
                                    valuePropRefs.current[index] = element;
                                }}
                                hideBody={adjustment.hideBody}
                                colliding={adjustment.isColliding}
                                translateY={adjustment.translateY}
                                contentLayout={adjustment.contentLayout}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
